import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import type { CanvasViewportState } from '../../context/TimelineContext'

export type CanvasMiniMapItem = {
  id: string
  x: number
  y: number
  isUrgentCrisisMode?: boolean
}

type CanvasMiniMapProps = {
  items: CanvasMiniMapItem[]
  viewportState: CanvasViewportState
  viewportSize: { width: number; height: number }
  worldSize: { width: number; height: number }
  onPanToCoordinates: (targetX: number, targetY: number) => void
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const CanvasMiniMap = ({
  items,
  viewportState,
  viewportSize,
  worldSize,
  onPanToCoordinates,
}: CanvasMiniMapProps) => {
  const mapRef = useRef<HTMLDivElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const bounds = useMemo(() => {
    const itemXs = items.map((item) => item.x).filter(Number.isFinite)
    const itemYs = items.map((item) => item.y).filter(Number.isFinite)
    const minX = Math.min(...itemXs, 0)
    const maxX = Math.max(...itemXs, worldSize.width || 1000)
    const minY = Math.min(...itemYs, 0)
    const maxY = Math.max(...itemYs, worldSize.height || 1000)
    const totalWidth = (maxX - minX) || 1000
    const totalHeight = (maxY - minY) || 1000

    return { minX, minY, maxX, maxY, totalWidth, totalHeight }
  }, [items, worldSize.height, worldSize.width])

  const dots = useMemo(() => items.map((item) => ({
    ...item,
    left: `${clamp(((item.x - bounds.minX) / bounds.totalWidth) * 100, 2, 98)}%`,
    top: `${clamp(((item.y - bounds.minY) / bounds.totalHeight) * 100, 2, 98)}%`,
  })), [bounds.minX, bounds.minY, bounds.totalHeight, bounds.totalWidth, items])

  const lens = useMemo(() => {
    const zoom = Math.max(viewportState.zoomScale || 1, 0.1)
    const width = clamp((viewportSize.width / zoom / bounds.totalWidth) * 100, 8, 100)
    const height = clamp((viewportSize.height / zoom / bounds.totalHeight) * 100, 10, 100)
    const left = clamp(((viewportState.panX - bounds.minX) / bounds.totalWidth) * 100, 0, 100 - width)
    const top = clamp(((viewportState.panY - bounds.minY) / bounds.totalHeight) * 100, 0, 100 - height)

    return { left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }
  }, [bounds.minX, bounds.minY, bounds.totalHeight, bounds.totalWidth, viewportState, viewportSize.height, viewportSize.width])

  const panFromPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const map = mapRef.current
    if (!map) return
    const rect = map.getBoundingClientRect()
    const percentX = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1)
    const percentY = clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1)
    const targetX = bounds.minX + (percentX * bounds.totalWidth)
    const targetY = bounds.minY + (percentY * bounds.totalHeight)
    onPanToCoordinates(targetX, targetY)
  }

  return (
    <div
      ref={mapRef}
      className="fixed bottom-6 right-6 z-40 h-32 w-48 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--surface-raised)] shadow-[var(--shadow-pop)]"
      aria-label="Checklist canvas mini map"
      role="presentation"
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
        setIsDragging(true)
        event.currentTarget.setPointerCapture(event.pointerId)
        panFromPointer(event)
      }}
      onPointerMove={(event) => {
        if (!isDragging) return
        event.preventDefault()
        event.stopPropagation()
        panFromPointer(event)
      }}
      onPointerUp={(event) => {
        event.preventDefault()
        event.stopPropagation()
        setIsDragging(false)
        event.currentTarget.releasePointerCapture(event.pointerId)
      }}
      onPointerCancel={(event) => {
        event.stopPropagation()
        setIsDragging(false)
      }}
    >
      <div className="absolute inset-0 bg-[var(--surface-muted)]" />
      {dots.map((item) => (
        <span
          key={item.id}
          className={`absolute h-[3px] w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full ${item.isUrgentCrisisMode ? 'animate-pulse bg-[var(--danger)] shadow-[var(--shadow-card)]' : 'bg-[var(--border)]'}`}
          style={{ left: item.left, top: item.top }}
        />
      ))}
      <span
        className="pointer-events-none absolute rounded border border-[var(--accent)] bg-[var(--accent-soft)] shadow-[var(--shadow-card)]"
        style={lens}
      />
      <span className="pointer-events-none absolute left-3 top-2 text-[8px] font-black uppercase tracking-[0.18em] text-[var(--muted)]">
        map
      </span>
    </div>
  )
}
